import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { StartQuote } from '@aica/api-types';
import { useToast } from '@/contexts/ToastContext';
import { logError } from '@/lib/logger';
import { useBacklogResultToast } from './useBacklogResultToast';
import type { ProcessingState } from './types';

/**
 * Ручной запуск с ценой до списания (CLAUDE.md, Analysis mode: «the price
 * shown before the charge»).
 *
 * Один путь для любого числа записей: массовый запуск из списка и кнопка
 * «Проанализировать» в карточке одной записи (#1389). Пока цену показывал
 * только массовый, одиночный запуск списывал молча — а после #1264 повтор
 * сбойной записи в ручном режиме оставляет её ждать именно этой кнопки.
 */

declare const quotedStartBrand: unique symbol;

/**
 * Набор, по которому показана цена. Создаёт его только этот хук, а
 * `ProcessingState.startRecords` принимает только его: запустить записи, не
 * показав цену, тип не даёт — следующая кнопка запуска не сможет молча
 * пропустить цену, как пропускала кнопка в карточке до #1389.
 */
export interface QuotedStart {
  /** Цена по записям, которые уйдут в запуск. */
  readonly quote: StartQuote;
  /** Тот же набор id, что был оценён: подтверждение запускает именно его, а
   * не пересчитывает выбор заново (за время открытого диалога он мог
   * измениться). */
  readonly recordIds: string[];
  readonly [quotedStartBrand]: true;
}

interface UseQuotedStartOptions {
  /** ProcessingState.quoteStart — цена набора id. */
  quoteStart: (recordIds: string[]) => Promise<StartQuote>;
  /** ProcessingState.startRecords — сама мутация запуска. */
  startRecords: ProcessingState['startRecords'];
  /**
   * Вызывается, когда набор запущен целиком, — с его id (массовый запуск
   * снимает с выбора именно их, а не то, что отметили, пока запрос шёл).
   */
  onConfirmed?: (recordIds: string[]) => void;
  /** Только для лога ошибки цены. */
  orgId?: string | null;
  /**
   * Чему принадлежит запрос: у одиночного запуска — открытая запись, у
   * массового — организация. Когда значение меняется, ещё не пришедшая цена
   * и уже открытый диалог отбрасываются: иначе человек, нажавший
   * «Проанализировать» на записи A и успевший открыть B (или сменить
   * организацию), увидел бы цену A поверх B и подтвердил бы списание за то,
   * чего уже не видит.
   */
  scopeKey?: string | null;
}

export interface UseQuotedStartReturn {
  dialog: QuotedStart | null;
  /** Окно между кликом и приходом цены — мутация запуска ещё не начата, но
   * второй клик поверх первого уже не нужен. */
  isQuoting: boolean;
  requestStart: (recordIds: string[]) => Promise<void>;
  confirm: () => void;
  close: () => void;
}

interface QuoteState {
  dialog: QuotedStart | null;
  quoting: boolean;
  scope: string | null | undefined;
}

function statusOf(error: unknown): number | undefined {
  return typeof error === 'object' && error !== null && 'status' in error
    ? (error as { status?: number }).status
    : undefined;
}

export function useQuotedStart({
  quoteStart,
  startRecords,
  onConfirmed,
  orgId,
  scopeKey,
}: UseQuotedStartOptions): UseQuotedStartReturn {
  const { t } = useTranslation(['records', 'common']);
  const { toast } = useToast();
  const { onError: onStartError, onPriceChanged } = useBacklogResultToast();
  // Ни одна запись не запускаема. Если это записи источника с остановленной
  // обработкой (#2709), они ждут — «ожидающих нет» было бы неправдой.
  const toastNothingToStart = useCallback(
    (quote: StartQuote) => {
      toast.info(
        t('records.start.nothingToStartTitle'),
        quote.processingPaused > 0
          ? t('records.start.resultProcessingPausedNote', {
              count: quote.processingPaused,
            })
          : t('records.start.nothingToStartBody')
      );
    },
    [toast, t]
  );
  const [state, setState] = useState<QuoteState>({
    dialog: null,
    quoting: false,
    scope: scopeKey,
  });

  // Смена области сбрасывает состояние прямо в рендере, а не эффектом: с
  // эффектом один кадр показывал бы новую запись под диалогом цены старой.
  if (state.scope !== scopeKey) {
    setState({ dialog: null, quoting: false, scope: scopeKey });
  }
  const current = state.scope === scopeKey ? state : null;

  // Область на момент ответа цены — ответ, пришедший после смены записи или
  // организации, не применяется. Номер запроса — тот же отсев для ответа,
  // который обогнал более поздний клик в той же области.
  const scopeRef = useRef(scopeKey);
  const requestSeq = useRef(0);
  useLayoutEffect(() => {
    scopeRef.current = scopeKey;
    // Смена области обнуляет и запросы в пути: иначе цена записи A, ушедшая
    // до перехода A → B → A, по возвращении открыла бы диалог без нового
    // клика — область снова та же, а номер запроса не сдвигался.
    requestSeq.current += 1;
  }, [scopeKey]);

  // Цена запрашивается в момент клика, а не заранее: запрос считает деньги
  // по живым тарифам, и цена «про запас» к моменту клика могла устареть.
  const requestStart = useCallback(
    async (recordIds: string[]) => {
      if (recordIds.length === 0) return;
      const seq = ++requestSeq.current;
      const scope = scopeRef.current;
      const isCurrent = () => seq === requestSeq.current && scope === scopeRef.current;
      setState((s) => ({ ...s, quoting: true }));
      try {
        const quote = await quoteStart(recordIds);
        if (!isCurrent()) return;
        // Ни одна запись не запускаема (почта, демо, пустой чат, запись,
        // которую уже забрал параллельный запуск): диалог «0 разговоров,
        // покрыто тарифом» с кнопкой «Начать» обещал бы анализ, которого не
        // будет. Говорим как есть.
        if (quote.records === 0) {
          setState((s) => ({ ...s, quoting: false }));
          toastNothingToStart(quote);
          return;
        }
        setState((s) => ({
          ...s,
          quoting: false,
          dialog: { quote, recordIds } as QuotedStart,
        }));
      } catch (err) {
        if (!isCurrent()) return;
        setState((s) => ({ ...s, quoting: false }));
        logError(
          'Failed to quote record start',
          { recordIds, orgId },
          err instanceof Error ? err : undefined
        );
        // 403 — тот же разбор, что у самого запуска: во встроенном режиме им
        // отвечает и CSRF-гейт при протухшей сессии, и различить его с
        // отказом по роли умеет только общий обработчик (по коду ответа).
        if (statusOf(err) === 403) {
          onStartError(err);
          return;
        }
        toast.error(
          t('records.start.quoteFailedTitle'),
          t('records.backlog.resultErrorBody')
        );
      }
    },
    [quoteStart, orgId, toast, t, onStartError, toastNothingToStart]
  );

  const dialog = current?.dialog ?? null;

  // Сама мутация не возвращает промис (ProcessingState.startRecords —
  // fire-and-forget): результат объявляет useBacklogResultToast.onStartSuccess.
  //
  // Сервер может не запустить набор: цена выросла, пока диалог был открыт
  // (#1396, например кончились минуты тарифа). Тогда диалог открывается снова —
  // с той же выборкой и новой ценой, а не молчаливым списанием. Если человек
  // за это время ушёл к другой записи или организации, новую цену поверх
  // чужого экрана не показываем (тот же отсев, что у ответа цены выше).
  const confirm = useCallback(() => {
    if (!dialog) return;
    // Тот же отсев, что у ответа цены: область И номер запроса. Одной области
    // мало — после перехода A → B → A она снова та же, и старый отказ открыл
    // бы диалог A без нового клика; смена области сдвигает номер.
    const seq = requestSeq.current;
    const scope = scopeRef.current;
    const { recordIds, quote: shown } = dialog;
    startRecords(dialog, {
      // Выбор снимается, когда запуск состоялся, а не когда его отправили:
      // отказ по цене открывает диалог снова, и выбор ему ещё нужен. И только
      // при ПОЛНОМ успехе — когда ушли все записи, которым названа цена
      // (`queued === quote.records`). Не «`skipped === 0`»: запускаемость
      // решает сервер, и письмо, демо-запись или чат без текста в выборке
      // всегда в `skipped` — выбор с ними не снимался бы никогда (code review
      // r2 на #1454). При частичном успехе ответ называет счётчики, а не id,
      // снять «только запущенные» нельзя — выбор остаётся, тост называет
      // остаток. Снимается весь набор этого запуска, как до #1396, а не всё
      // отмеченное: выбранное, пока запрос шёл, остаётся.
      onStarted: onConfirmed
        ? (result) => {
            if (result.failed === 0 && result.queued === shown.records) {
              onConfirmed(recordIds);
            }
          }
        : undefined,
      onPriceChanged: (quote) => {
        // Человек ушёл к другой записи или организации: новую цену поверх
        // чужого экрана не показываем, но и не молчим — общий обработчик
        // этот отказ пропускает (`useProcessingState`), и без тоста запуск,
        // который не состоялся, выглядел бы идущим (code review r2 на #1454).
        if (seq !== requestSeq.current || scope !== scopeRef.current) {
          toast.info(
            t('records.start.priceChangedTitle'),
            t('records.start.priceChangedNotShownBody')
          );
          return;
        }
        if (quote.records === 0) {
          toastNothingToStart(quote);
          return;
        }
        setState((s) => ({ ...s, dialog: { quote, recordIds } as QuotedStart }));
        onPriceChanged();
      },
    });
    setState((s) => ({ ...s, dialog: null }));
  }, [dialog, startRecords, onConfirmed, toast, t, onPriceChanged, toastNothingToStart]);

  const close = useCallback(() => setState((s) => ({ ...s, dialog: null })), []);

  return { dialog, isQuoting: current?.quoting ?? false, requestStart, confirm, close };
}
